import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { base44 } from "@/api/base44Client";
import { supabase } from "@/api/supabaseClient";
import { env } from "@/lib/env";
import { clearVerifiedUser, readVerifiedUser, writeVerifiedUser } from "@/lib/offlineAuthCache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const checkUserAuth = useCallback(async (options = {}) => {
    const background = options?.background === true;
    if (!env.isSupabaseConfigured) {
      setUser(null);
      setIsLoadingAuth(false);
      setAuthChecked(true);
      return null;
    }
    if (!background) setIsLoadingAuth(true);
    setAuthError(null);
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        setUser(null);
        clearVerifiedUser(sessionStorage);
        return null;
      }
      const currentUser = await base44.auth.me();
      setUser(currentUser);
      writeVerifiedUser(sessionStorage, currentUser);
      return currentUser;
    } catch (error) {
      // A refresh must not stop an in-progress pitch workflow when connectivity
      // drops. This tab-scoped value was populated only after a server check;
      // it gates cached UI, never database authorization. RLS and protected
      // scoring operations still revalidate the account on reconnection.
      const offlineUser = navigator.onLine === false ? readVerifiedUser(sessionStorage) : null;
      if (offlineUser) {
        setUser(offlineUser);
        setAuthError(null);
        return offlineUser;
      }
      setUser(null);
      const message = error?.message || "Could not verify your account";
      if (!message.toLowerCase().includes("session")) {
        const type = message.toLowerCase().includes("disabled")
          ? "account_disabled"
          : message.toLowerCase().includes("provisioned") ? "account_unprovisioned" : "unknown";
        setAuthError({ type, message });
      }
      return null;
    } finally {
      if (!background) setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    checkUserAuth();
    if (!env.isSupabaseConfigured) return undefined;
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setUser(null);
        clearVerifiedUser(sessionStorage);
        setIsLoadingAuth(false);
        setAuthChecked(true);
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        queueMicrotask(checkUserAuth);
      }
    });
    // Revalidate without replacing the entire routed app with a spinner. An
    // offline score queue may need the mounted staff screen to flush at this
    // exact moment.
    const revalidateOnline = () => checkUserAuth({ background: true });
    window.addEventListener("online", revalidateOnline);
    return () => {
      data.subscription.unsubscribe();
      window.removeEventListener("online", revalidateOnline);
    };
  }, [checkUserAuth]);

  const logout = useCallback(async (shouldRedirect = true) => {
    setUser(null);
    clearVerifiedUser(sessionStorage);
    await base44.auth.logout(shouldRedirect ? "/login" : null);
  }, []);

  const navigateToLogin = useCallback(() => {
    base44.auth.redirectToLogin(window.location.href);
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated: Boolean(user),
    isLoadingAuth,
    isLoadingPublicSettings: false,
    authError,
    appPublicSettings: { id: "matchbase", public_settings: { backend: "supabase" } },
    authChecked,
    logout,
    navigateToLogin,
    checkUserAuth,
    checkAppState: checkUserAuth,
    backendConfigured: env.isSupabaseConfigured,
  }), [authChecked, authError, checkUserAuth, isLoadingAuth, logout, navigateToLogin, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
